import { useGTM } from "gtm-react-hook";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ErrorPanel } from "@/components/atoms";
import { useLayout, useOperator, useOperatorAccess, useProfile } from "@/hooks";
import { clsx } from "@/utils";
import { Header } from "..";

/** Routes an unsubscribed operator may still reach; everything else redirects to the lock pane. */
const UNSUBSCRIBED_ALLOWED_SEGMENTS = ["/payments", "/config", "/subscription-required", "/profile", "/operators"];

export const PrivateContent = () => {
  const {
    layout: { sidebarOpen, fullScreen },
  } = useLayout();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { eventGTM } = useGTM();
  const { id: profileId } = useProfile();
  const { providerCode, flags } = useOperator();
  const { isUnsubscribed } = useOperatorAccess();

  // Keep unsubscribed operators on the lock pane / allowed routes even when navigating by URL.
  useEffect(() => {
    if (isUnsubscribed && !UNSUBSCRIBED_ALLOWED_SEGMENTS.some((segment) => pathname.includes(segment))) {
      navigate("/subscription-required");
    }
  }, [isUnsubscribed, pathname, navigate]);

  useEffect(() => {
    if (flags.isSuspended) navigate("/suspended");

    eventGTM("identifyUser", {
      accountUuid: profileId,
      providerKey: providerCode,
    });

    // console.log("Tracking: User Identified", {
    //   id: profileId,
    //   name: `${firstName} ${lastName}`,
    //   email,
    //   company: {
    //     id: operatorId,
    //     name: providerCode,
    //   },
    //   role: roles?.length > 0 ? roles[0] : undefined,
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerCode]);

  // useEffect(() => {
  // console.log("Tracking: Page Reload", location.pathname);
  // }, [location]);

  if (fullScreen) return <Outlet />;

  return (
    <div
      className={clsx("flex flex-1 flex-col lg:min-w-[768px]", {
        "lg:pl-[85px]": !sidebarOpen,
        "lg:pl-[270px]": sidebarOpen,
      })}
    >
      <Header />

      <ErrorBoundary fallback={<ErrorPanel messages={["Some error occured."]} />}>
        {/* Keep <main> full-bleed: pages that paint a full-width background (e.g. View Client's gray band,
            DEV-1177) break out of the px via -mx and must reach the viewport edge on ultrawide. The
            ultrawide content cap (DEV-1367) lives per-page on the content column (see Bookings' max-w-[2360px]
            wrapper; Clients/Companies/Drivers already self-cap via Container), NOT here — wrapping the Outlet
            in a capped Container clipped those full-bleed backgrounds and left white side-gutters. */}
        <main className="flex flex-col items-stretch bg-white px-3 sm:min-h-[calc(100vh-65px)] lg:px-5">
          <Outlet />
        </main>
      </ErrorBoundary>
    </div>
  );
};
